<template>
  <Drawer
    :id="drawerId"
    title="Create A New Project"
    width="95%"
  >
    <v-progress-circular
      v-if="!loaded"
      :size="125"
      color="primary"
      indeterminate
      class="loader"
    />
    <div
      v-if="galleryUrl"
      class="d-flex h-full"
    >
      <!-- :key so a new link REBUILDS the iframe instead of navigating the one already there. An iframe
           navigation lands in the top window's joint session history, so navigating it would spend one of the
           dashboard's Back presses every time the drawer was reopened. A fresh element's first load replaces
           rather than pushes. -->
      <iframe
        ref="gallery"
        :key="galleryUrl"
        :src="galleryUrl"
        class="w-full h-full"
        style="border: none"
        @load="loaded = true"
      />
    </div>
  </Drawer>
</template>

<script>
import { mapGetters } from 'vuex';
import Drawer from '../../components/layout/Drawer';
import { DRAWERS, GALLERY_MESSAGES } from '../../utils/constants';

/**
 * The template gallery (plans/template-gallery.md), in place of the website catalog this drawer used to frame: a
 * gallery link for the signed-in user from DashboardApi (project/getGalleryUrl), optionally on a category or a size
 * (`categoryId` / `coreProductId` in the drawer's data). The gallery decides what they may see from the link's token.
 * A pick is told to this page; it goes to the editor link it was given, the way the old catalog's pick did.
 */
export default {
  name: 'CatalogDrawer',

  components: {
    Drawer
  },

  props: {
    iframed: Boolean
  },

  data() {
    return {
      drawerId: DRAWERS.CATALOG_DRAWER,
      galleryUrl: '',
      galleryOrigin: '',
      loaded: false
    };
  },

  computed: {
    ...mapGetters({
      initialData: 'drawers/data',
      drawerOpenedAt: 'drawers/lastOpenedAt'
    })
  },

  watch: {
    drawerOpenedAt() {
      if (this.drawerId === this.$store.getters['drawers/topDrawer']?.id) this.openGallery();
    }
  },

  mounted() {
    window.addEventListener('message', this.onMessage);
  },

  beforeDestroy() {
    window.removeEventListener('message', this.onMessage);
  },

  methods: {
    openGallery() {
      const data = this.initialData(this.drawerId) || {};
      const params = {};
      if (data.coreProductId) params.coreProductId = data.coreProductId;
      else if (data.categoryId) params.categoryId = data.categoryId;

      this.loaded = false;
      this.$api
        .get('project/getGalleryUrl', { params })
        .then((response) => {
          this.galleryOrigin = new URL(response.data.url).origin;
          this.galleryUrl = response.data.url;
        })
        .catch((error) => {
          this.loaded = true;
          this.logError(error, 'An error occurred while opening the templates');
        });
    },

    /**
     * Only the gallery in this drawer, from the editor's origin, and only a link to the editor or to this dashboard (a
     * lapsed subscriber's pick answers with their subscription page here, as the old catalog's did).
     */
    onMessage(e) {
      if (!this.galleryOrigin || e.origin !== this.galleryOrigin) return;
      if (!this.$refs.gallery || e.source !== this.$refs.gallery.contentWindow) return;
      if (e.data?.type !== GALLERY_MESSAGES.PICKED) return;

      const url = e.data.url;
      const allowed = [this.galleryOrigin + '/', window.location.origin + '/'];
      if (typeof url === 'string' && allowed.some((x) => url.startsWith(x))) window.location.href = url;
    }
  }
};
</script>

<style scoped>
.loader {
  position: absolute;
  left: calc(50% - 62px);
  top: calc(50% - 62px);
}
</style>
